import React, { useMemo } from 'react';
import { Tabs, Tooltip, Collapse, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { WatchImpactSection } from './WatchImpactSection';

/**
 * Rendu d'un rapport de veille : une section markdown de niveau 2 (##) par
 * onglet — SAUF les trois sections de contexte (numérotées I. / II. / III.
 * par le prompt, numérotation conservée dans toutes les langues), regroupées
 * dans un onglet « Contexte » unique en trois blocs repliables.
 *
 * Le label d'onglet est le titre de section débarrassé de son préfixe romain
 * et de la partie après le tiret. Le préambule (avant la première section)
 * est fusionné dans le premier onglet, sans son H1 (redondant avec la page).
 */

const sectionLabel = (title: string) => {
	let label = title.replace(/^[IVX]+\.\s*/i, '');
	const dash = label.search(/\s[—–-]\s/);
	if (dash > 2) label = label.slice(0, dash);
	return label.trim();
};

const isContextSection = (title: string) => /^(I|II|III)\.\s/.test(title);

const splitSections = (md: string) => {
	const chunks = (md || '').split(/\n(?=## )/);
	let preamble = chunks[0]?.startsWith('## ') ? '' : (chunks.shift() || '');
	// H1 redondant (le nom du rapport est déjà dans la page/modale).
	preamble = preamble.replace(/^#\s[^\n]*\n/, '').trim();
	const sections = chunks.map((c) => {
		const nl = c.indexOf('\n');
		const title = c.slice(3, nl > 0 ? nl : undefined).trim();
		const body = nl > 0 ? c.slice(nl + 1) : '';
		return { title, label: sectionLabel(title), body };
	});
	if (sections.length && preamble) {
		sections[0].body = `${preamble}\n\n${sections[0].body}`;
	}
	return sections;
};

const Md = ({ children }: { children: string }) => (
	<ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
);

export const WatchReportView = ({ report, meta, onGenerateScenario }: { report: any; meta?: React.ReactNode; onGenerateScenario?: (reportTitle: string) => void }) => {
	const { t } = useTranslation();
	const sections = useMemo(() => splitSections(report?.content || ''), [report?.content]);

	if (!sections.length) {
		return (
			<div className="watch-report">
				{meta}
				<Md>{report?.content || ''}</Md>
			</div>
		);
	}

	const contextSections = sections.filter((s) => isContextSection(s.title));
	const items: any[] = [];
	let contextInserted = false;

	sections.forEach((s, i) => {
		if (contextSections.length > 1 && isContextSection(s.title)) {
			if (contextInserted) return;
			contextInserted = true;
			items.push({
				key: 'context',
				label: t('pages.watch.context_tab'),
				children: (
					<div className="watch-section">
						<Collapse
							defaultActiveKey={contextSections.map((_, j) => String(j))}
							items={contextSections.map((cs, j) => ({
								key: String(j),
								label: <strong>{cs.title.replace(/^[IVX]+\.\s*/i, '')}</strong>,
								children: <Md>{cs.body}</Md>
							}))}
						/>
					</div>
				)
			});
			return;
		}
		items.push({
			key: String(i),
			label: <Tooltip title={s.title}>{s.label}</Tooltip>,
			children: (
				<div className="watch-section">
					<Md>{s.body}</Md>
				</div>
			)
		});
	});

	if (onGenerateScenario) {
		// Cherche la section d'impact projet générée par Bedrock (titre contenant "impact")
		// pour la co-localiser avec le bouton "Générer un scénario" dans le même onglet.
		const impactSectionIdx = sections.findIndex((s) =>
			/impact/i.test(s.title) && !/^(I|II|III|IV|V|VI|VII)\.\s/i.test(s.title)
		);
		const impactBody = impactSectionIdx >= 0 ? sections[impactSectionIdx].body : null;

		// Retirer la section impact des onglets réguliers (déjà ajoutés) pour éviter le doublon
		if (impactBody) {
			const impactKey = String(impactSectionIdx);
			const idx = items.findIndex((item) => item.key === impactKey);
			if (idx >= 0) items.splice(idx, 1);
		}

		items.push({
			key: 'impact',
			label: <Tooltip title={t('pages.watch.impact_tab_tooltip', { defaultValue: 'Analyse de l\'impact sur votre projet et génération de scénario' })}>{t('pages.watch.impact_tab', { defaultValue: 'Impact projet' })}</Tooltip>,
			children: (
				<div className="watch-section watch-impact-tab">
					{impactBody && <WatchImpactSection body={impactBody} />}
					{!impactBody && (
						<p style={{ color: '#595959', fontSize: 13 }}>
							{t('pages.watch.impact_description', { defaultValue: 'Générez un scénario stratégique basé sur les tendances identifiées dans ce rapport de veille.' })}
						</p>
					)}
					<div style={{ marginTop: impactBody ? 24 : 0, paddingTop: impactBody ? 16 : 0, borderTop: impactBody ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
						<Button
							type="primary"
							icon={<FontAwesomeIcon icon={faWandMagicSparkles as any} style={{ marginRight: 6 }} />}
							onClick={() => onGenerateScenario(report?.title || '')}
						>
							{t('pages.watch.actions.generate_scenario', { defaultValue: 'Générer un scénario à partir de ce rapport' })}
						</Button>
					</div>
				</div>
			)
		});
	}

	return (
		<div className="watch-report">
			{meta}
			<Tabs type="card" className="watch-report-tabs" items={items} />
		</div>
	);
};

export default WatchReportView;
